import { createBrowserRouter, Navigate } from "react-router";
import { i18nNamespace, loadLanguagePacks } from "i18n";
import { Reservation } from "reservation";
import { RouteErrorElement } from "shared/components/layouts/RouteErrorElement";
import { Root } from "./Root";
import { StorefrontViewLayout } from "./apps/storefront/StorefrontViewLayout";

// TODO: Reminder to update to React Router v7
export const router = createBrowserRouter([
  {
    path: "/",
    errorElement: <RouteErrorElement />,
    lazy: () =>
      import("./onboarding/Onboarding").then(module => ({
        Component: module.Onboarding,
      })),
    loader: async () =>
      loadLanguagePacks([i18nNamespace.Core, i18nNamespace.Onboarding]),
  },
  {
    path: "/order/:orderId",
    Component: Reservation,
    // Public payer routes sit outside `/app` (no RootLayout boundary). Catch
    // loader failures with the same ErrorBoundaryPage UI; render errors are
    // handled inside PublicPayerPageShell's PostHogErrorBoundary.
    errorElement: <RouteErrorElement />,
    loader: async () =>
      loadLanguagePacks([
        i18nNamespace.Core,
        i18nNamespace.Booker,
        i18nNamespace.Storefront,
      ]),
  },
  {
    path: "/invoice/:invoiceNumber",
    lazy: () =>
      import("./paymentRequest/Invoice").then(module => ({
        Component: module.Invoice,
      })),
    errorElement: <RouteErrorElement />,
    loader: async () =>
      loadLanguagePacks([
        i18nNamespace.Core,
        i18nNamespace.Booker,
        i18nNamespace.Storefront,
      ]),
  },
  {
    path: "/app",
    // Child loader / lazy-import failures land here. Do not redirect to `/`:
    // onboarding sends signed-in users straight back to `/app`, which loops.
    errorElement: <RouteErrorElement />,
    Component: Root,
    loader: async () => loadLanguagePacks([i18nNamespace.Core]),
    children: [
      {
        path: "",
        lazy: () =>
          import("App").then(module => ({
            Component: module.default,
          })),
        loader: async () => loadLanguagePacks([i18nNamespace.Core]),
      },
      {
        path: "welcome",
        lazy: () =>
          import("./onboarding/PostOnboarding").then(module => ({
            Component: module.PostOnboarding,
          })),
        loader: async () =>
          loadLanguagePacks([i18nNamespace.Core, i18nNamespace.PostOnboarding]),
      },
      {
        path: "admin",
        lazy: () =>
          import("./apps/adminland/Adminland").then(module => ({
            Component: module.Adminland,
          })),
        loader: async () =>
          loadLanguagePacks([
            i18nNamespace.Core,
            i18nNamespace.Adminland,
            i18nNamespace.People,
            i18nNamespace.Home,
            i18nNamespace.Widgets,
            i18nNamespace.Tripper,
            i18nNamespace.Events,
          ]),
      },
      {
        path: "organization/:organizationId",
        children: [
          {
            path: "home",
            lazy: () =>
              import("./apps/home").then(module => ({
                Component: module.OrganizationHome,
              })),
            loader: async () =>
              loadLanguagePacks([
                i18nNamespace.Core,
                i18nNamespace.Home,
                i18nNamespace.Widgets,
                i18nNamespace.Adminland,
                i18nNamespace.People,
              ]),
          },
          {
            path: "directory",
            lazy: () =>
              import("./apps/directory/Directory").then(module => ({
                Component: module.Directory,
              })),
            loader: async () =>
              loadLanguagePacks([
                i18nNamespace.Core,
                i18nNamespace.Directory,
                i18nNamespace.Adminland,
                i18nNamespace.People,
              ]),
          },
          {
            path: "directory/:operatorEntityId",
            lazy: () =>
              import("./apps/directory/Operator").then(module => ({
                Component: module.Operator,
              })),
            loader: async () =>
              loadLanguagePacks([
                i18nNamespace.Core,
                i18nNamespace.Directory,
                i18nNamespace.Adminland,
                i18nNamespace.People,
              ]),
          },
          {
            path: "profile",
            lazy: () =>
              import("./apps/directory/OrganizationProfile").then(module => ({
                Component: module.OrganizationProfile,
              })),
            loader: async () =>
              loadLanguagePacks([
                i18nNamespace.Core,
                i18nNamespace.Directory,
                i18nNamespace.Adminland,
                i18nNamespace.People,
              ]),
          },
          {
            path: "custom-lists",
            lazy: () =>
              import("./apps/customLists/CustomLists").then(module => ({
                Component: module.CustomLists,
              })),
            loader: async () =>
              loadLanguagePacks([
                i18nNamespace.Core,
                i18nNamespace.CustomLists,
                i18nNamespace.Adminland,
                i18nNamespace.People,
              ]),
          },
          {
            path: "custom-lists/:listId",
            lazy: () =>
              import("./apps/customLists/CustomListDetails").then(module => ({
                Component: module.CustomListDetails,
              })),
            loader: async () =>
              loadLanguagePacks([
                i18nNamespace.Core,
                i18nNamespace.CustomLists,
                i18nNamespace.Adminland,
                i18nNamespace.People,
              ]),
          },
          {
            path: "widgets",
            lazy: () =>
              import("./apps/widgets/Widgets").then(module => ({
                Component: module.Widgets,
              })),
            loader: async () =>
              loadLanguagePacks([
                i18nNamespace.Core,
                i18nNamespace.Widgets,
                i18nNamespace.CustomLists,
                i18nNamespace.Events,
                i18nNamespace.Adminland,
                i18nNamespace.People,
              ]),
          },
          {
            path: "widgets/activity",
            lazy: () =>
              import("./apps/widgets/views/WidgetEmbeddingActivityOrg").then(
                module => ({
                  Component: module.WidgetEmbeddingActivityOrg,
                }),
              ),
            loader: async () =>
              loadLanguagePacks([
                i18nNamespace.Core,
                i18nNamespace.Widgets,
                i18nNamespace.CustomLists,
                i18nNamespace.Events,
                i18nNamespace.Adminland,
                i18nNamespace.People,
              ]),
          },
          {
            path: "widgets/:widgetId",
            lazy: () =>
              import("./apps/widgets/WidgetDetails").then(module => ({
                Component: module.WidgetDetails,
              })),
            loader: async () =>
              loadLanguagePacks([
                i18nNamespace.Core,
                i18nNamespace.CustomLists,
                i18nNamespace.Widgets,
                i18nNamespace.Events,
                i18nNamespace.Adminland,
                i18nNamespace.People,
              ]),
          },
          {
            path: "events",
            lazy: () =>
              import("./apps/events/Events").then(module => ({
                Component: module.Events,
              })),
            loader: async () =>
              loadLanguagePacks([
                i18nNamespace.Core,
                i18nNamespace.Events,
                i18nNamespace.Adminland,
                i18nNamespace.People,
              ]),
          },
          {
            path: "people",
            lazy: () =>
              import("./apps/people/People").then(module => ({
                Component: module.People,
              })),
            loader: async () =>
              loadLanguagePacks([
                i18nNamespace.Core,
                i18nNamespace.Adminland,
                i18nNamespace.People,
                i18nNamespace.QueryBuilder,
              ]),
          },
          {
            path: "tripper",
            lazy: () =>
              import("./apps/tripper").then(module => ({
                Component: module.Tripper,
              })),
            loader: async () =>
              loadLanguagePacks([
                i18nNamespace.Core,
                i18nNamespace.Tripper,
                i18nNamespace.Directory,
              ]),
          },
          {
            path: "tripper/:itineraryId",
            lazy: () =>
              import("./apps/tripper").then(module => ({
                Component: module.Tripper,
              })),
            loader: async () =>
              loadLanguagePacks([
                i18nNamespace.Core,
                i18nNamespace.Tripper,
                i18nNamespace.Directory,
              ]),
          },
          // {
          //   path: "settings",
          //   element: <Settings />,
          //   loader: async () =>
          //     loadLanguagePacks([i18nNamespace.Core, i18nNamespace.Settings]),
          // },
          {
            path: "storefront/:storefrontId",
            Component: StorefrontViewLayout,
            children: [
              {
                path: "",
                lazy: () =>
                  import("./apps/storefront").then(module => ({
                    Component: module.Storefront,
                  })),
                loader: async () =>
                  loadLanguagePacks([
                    i18nNamespace.Core,
                    i18nNamespace.Storefront,
                    i18nNamespace.Adminland,
                    i18nNamespace.People,
                  ]),
              },
              {
                path: "order/:orderId",
                lazy: () =>
                  import(
                    "./apps/storefront/OrderDetails/OrderDetails"
                  ).then(module => ({
                    Component: module.OrderDetails,
                  })),
                loader: async () =>
                  loadLanguagePacks([
                    i18nNamespace.Core,
                    i18nNamespace.Booker,
                    i18nNamespace.Storefront,
                  ]),
              },
              {
                path: "invoice/:paymentRequestId",
                lazy: () =>
                  import(
                    "./apps/storefront/OrderDetails/InvoiceDetails"
                  ).then(module => ({
                    Component: module.InvoiceDetails,
                  })),
                loader: async () =>
                  loadLanguagePacks([
                    i18nNamespace.Core,
                    i18nNamespace.Storefront,
                  ]),
              },
              {
                path: ":bookerId",
                lazy: () =>
                  import(
                    "./apps/storefront/setupBookingWidget/CreateEditBookingWidget"
                  ).then(module => ({
                    Component: module.CreateEditBookingWidget,
                  })),
                loader: async () =>
                  loadLanguagePacks([
                    i18nNamespace.Core,
                    i18nNamespace.Storefront,
                  ]),
              },
            ],
          },
          {
            path: "",
            element: <Navigate to="home" replace={true} />,
          },
        ],
      },
      {
        path: "operator",
        children: [
          {
            path: "",
            lazy: () =>
              import("./apps/home").then(module => ({
                Component: module.Home, // Redirect to the home page
              })),
            loader: async () =>
              loadLanguagePacks([i18nNamespace.Core, i18nNamespace.Directory]),
          },
          {
            path: ":operatorEntityId",
            lazy: () =>
              import("./apps/directory/Operator").then(module => ({
                Component: module.Operator,
              })),
            loader: async () =>
              loadLanguagePacks([i18nNamespace.Core, i18nNamespace.Directory]),
          },
        ],
      },
      {
        path: "storefront/:storefrontId",
        Component: StorefrontViewLayout,
        children: [
          {
            path: "",
            lazy: () =>
              import("./apps/storefront").then(module => ({
                Component: module.Storefront,
              })),
            loader: async () =>
              loadLanguagePacks([
                i18nNamespace.Core,
                i18nNamespace.Storefront,
                i18nNamespace.Adminland,
                i18nNamespace.People,
              ]),
          },
          {
            path: "order/:orderId",
            lazy: () =>
              import("./apps/storefront/OrderDetails/OrderDetails").then(
                module => ({
                  Component: module.OrderDetails,
                }),
              ),
            loader: async () =>
              loadLanguagePacks([
                i18nNamespace.Core,
                i18nNamespace.Booker,
                i18nNamespace.Storefront,
              ]),
          },
          {
            path: "invoice/:paymentRequestId",
            lazy: () =>
              import("./apps/storefront/OrderDetails/InvoiceDetails").then(
                module => ({
                  Component: module.InvoiceDetails,
                }),
              ),
            loader: async () =>
              loadLanguagePacks([
                i18nNamespace.Core,
                i18nNamespace.Storefront,
              ]),
          },
          {
            path: ":bookerId",
            lazy: () =>
              import(
                "./apps/storefront/setupBookingWidget/CreateEditBookingWidget"
              ).then(module => ({
                Component: module.CreateEditBookingWidget,
              })),
            loader: async () =>
              loadLanguagePacks([i18nNamespace.Core, i18nNamespace.Storefront]),
          },
        ],
      },
    ],
  },
  {
    path: "*",
    errorElement: <RouteErrorElement />,
    element: <Navigate to="/" replace={true} />,
  },
]);
